/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useMemo } from 'react';
import { Link } from '@/components/link';
import { User } from 'lucide-react';
import { useAuth } from '@/providers/auth';
import { Button } from '@/components/buttons';
import { useTranslation } from 'react-i18next';
import { UserMenu } from './user-menu';

export default function UserActions(): ReactElement {
    const session = useAuth();
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');
    const isAuthenticated: boolean = useMemo(() => {
        // Check if user is authenticated (has valid token and is registered)
        return Boolean(session?.userType === 'registered' && session?.customerId);
    }, [session]);

    const accountLink = isAuthenticated ? '/account/overview' : '/login';
    const label = isAuthenticated ? tAccount('myAccount') : t('logIn');
    const icon = <User className="size-5" aria-hidden="true" />;

    const trigger = (
        <Button
            variant="ghost"
            className="hidden lg:flex cursor-pointer gap-1 px-4 normal-case tracking-normal hover:bg-transparent hover:opacity-50 transition-opacity"
            asChild>
            <Link to={accountLink} aria-label={label}>
                {icon}
                <span className="hidden text-xs font-semibold leading-4 lg:inline">{label}</span>
            </Link>
        </Button>
    );

    return <UserMenu isAuthenticated={isAuthenticated} trigger={trigger} />;
}
